import React from "react";
import { Cpu, Scale, Compass } from "lucide-react";
import CircleGlow from "../components/CircleGlow";

/**
 * /team — "De mensen achter RiskaiΛ"
 *
 * Editorial team page: hero header + three high-end profile cards.
 * Pure Lounge Doctrine — #1C3A5A canvas, Fraunces serif headings,
 * silver hairlines, soft cyan glow. Avatars are monogram portraits
 * (initials inside a silver ring) so the page can ship without
 * hallucinated stock photography.
 */

const PORTRAIT_SIZE = 240;

const PROFILES = [
  {
    id: "erik-nijenhuis",
    initials: "EN",
    name: "Erik Nijenhuis",
    role: "Chief Technical Officer",
    abbr: "CTO",
    icon: Cpu,
    discipline: "Architecture · Linux · Precision",
    photo:
      "https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/3488db74eaff465791d0e2ecb1b44006_Screenshot%202026-05-23%2011.18.13.png",
    body:
      "Erik is de technische ruggengraat van RiskaiΛ. Hij vertaalt complexe " +
      "governance-vraagstukken naar onderhoudbare, high-quality Linux-software " +
      "en bewaakt de architectuur tot op het laatste karakter. Zijn voorliefde " +
      "voor LaTeX-documentatie zorgt ervoor dat elk component reproduceerbaar, " +
      "auditbaar en wetenschappelijk verantwoord wordt opgeleverd.",
  },
  {
    id: "germen-haijtema",
    initials: "GH",
    name: "Germen Haijtema",
    role: "Chief Compliance Officer",
    abbr: "CCO",
    icon: Scale,
    discipline: "Recht · Regie · EU AI Act",
    photo:
      "https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/1e0405919d554a39a9d43b9fdba1c08e_Screenshot%202026-05-23%2011.15.32.png",
    body:
      "Germen is het juridische geweten van het platform. Hij ontrafelt " +
      "complexe wet- en regelgeving — van de AVG en de Algemene wet bestuursrecht " +
      "tot de Europese AI-Verordening — en vertaalt die naar werkbare " +
      "operationele bedrijfsprocessen. Onder zijn leiding is RiskaiΛ future-proof " +
      "ten opzichte van de strengste toezichthouders.",
  },
  {
    id: "ben-stegeman",
    initials: "BS",
    name: "Ben Stegeman",
    role: "Chief Visionary Officer",
    abbr: "CVO",
    icon: Compass,
    discipline: "Visie · Strategie · Maatschappij",
    photo:
      "https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/fc955a7ce4c14a929f6d455913985742_Screenshot%202026-05-23%2011.12.44.png",
    body:
      "Ben is de strategische visionair die de brug slaat tussen wetgeving en " +
      "maatschappelijke impact. Met een achtergrond in financiële " +
      "dienstverlening en als professioneel auteur ziet hij verbanden waar " +
      "anderen silo's zien. Hij bewaakt de richting van RiskaiΛ — altijd " +
      "geworteld in de waarden van de maatschappij.",
  },
];

const Team = () => (
  <div data-testid="page-team">
    {/* ============================================================
        HERO
       ============================================================ */}
    <section className="relative overflow-hidden" data-testid="team-hero">
      <CircleGlow intensity={0.9} />
      <div className="rk-container relative pt-24 md:pt-28 pb-12 md:pb-16">
        <div className="overline mb-6" data-testid="team-eyebrow">
          De mensen · achter het platform
        </div>
        <h1
          className="text-white tracking-tight leading-[1.05] text-5xl md:text-7xl max-w-4xl"
          style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
          data-testid="team-title"
        >
          De mensen achter{" "}
          <em
            className="italic text-[#D4D8DF] rk-wordmark"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            RiskaiΛ.
          </em>
        </h1>
        <p
          className="mt-8 max-w-3xl text-lg md:text-xl text-[#D4D8DF] leading-relaxed"
          data-testid="team-subtitle"
        >
          Een unieke combinatie van strategische visie, juridisch geweten en
          technische perfectie.
        </p>
      </div>
      <div className="hairline" />
    </section>

    {/* ============================================================
        PROFILES
       ============================================================ */}
    <section
      className="relative overflow-hidden"
      data-testid="team-profiles"
    >
      <div className="rk-container relative py-20 md:py-28">
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#C0C5CE]/15"
          data-testid="team-profiles-grid"
        >
          {PROFILES.map((p) => (
            <ProfileCard key={p.id} profile={p} />
          ))}
        </div>
      </div>
      <div className="hairline" />
    </section>

    {/* ============================================================
        FOOTNOTE
       ============================================================ */}
    <section className="relative overflow-hidden">
      <CircleGlow intensity={0.5} />
      <div className="rk-container relative py-20 md:py-24 text-center">
        <p
          className="max-w-3xl mx-auto text-xl md:text-2xl text-white tracking-tight leading-snug"
          style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
          data-testid="team-footnote"
        >
          Drie disciplines, één doel:{" "}
          <em
            className="italic"
            style={{ color: "#7FD7E4", fontFamily: "Fraunces, serif" }}
          >
            algoritmes die de mienskip dienen
          </em>
          {" "}— niet andersom.
        </p>
      </div>
    </section>
  </div>
);

// ---------- ProfileCard ----------
const ProfileCard = ({ profile }) => {
  const Icon = profile.icon;
  return (
    <article
      data-testid={`team-profile-${profile.id}`}
      className="bg-[#162E4A] hover:bg-[#18335A] transition-colors duration-500 p-10 md:p-12 flex flex-col"
    >
      {/* Portrait — photo when supplied, monogram fallback otherwise */}
      <div
        className="relative mx-auto"
        data-testid={`team-portrait-${profile.id}`}
        style={{ width: PORTRAIT_SIZE, height: PORTRAIT_SIZE }}
        aria-hidden
      >
        {/* Cyan glow halo */}
        <div
          className="absolute -inset-3 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 50% 40%, rgba(91,192,235,0.18) 0%, rgba(91,192,235,0) 70%)",
          }}
        />
        {/* Frame */}
        <div
          className="absolute inset-0 rounded-full overflow-hidden flex items-center justify-center"
          style={{
            border: "1px solid rgba(192,197,206,0.55)",
            background:
              "linear-gradient(160deg, rgba(255,255,255,0.04) 0%, rgba(91,192,235,0.04) 100%)",
            boxShadow:
              "inset 0 0 30px rgba(91,192,235,0.10), 0 22px 60px -24px rgba(91,192,235,0.40)",
          }}
        >
          {profile.photo ? (
            <img
              src={profile.photo}
              alt={profile.name}
              draggable={false}
              data-testid={`team-portrait-img-${profile.id}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center top",
                filter:
                  "grayscale(1) contrast(1.05) brightness(0.95)",
                display: "block",
              }}
            />
          ) : (
            <span
              className="text-white"
              style={{
                fontFamily: "Fraunces, serif",
                fontWeight: 300,
                fontSize: "4rem",
                letterSpacing: "0.04em",
              }}
            >
              {profile.initials}
            </span>
          )}
        </div>
        {/* Discipline icon badge */}
        <div
          className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: "#0F1E33",
            border: "1px solid rgba(192,197,206,0.55)",
          }}
        >
          <Icon strokeWidth={1} className="w-5 h-5 text-[#7FD7E4]" />
        </div>
      </div>

      {/* Role overline */}
      <div
        className="mt-12 text-center text-[11px] tracking-[0.28em] uppercase"
        style={{ color: "#7FD7E4" }}
      >
        {profile.role} · {profile.abbr}
      </div>

      {/* Name */}
      <h3
        className="mt-4 text-center text-3xl md:text-[2rem] text-white tracking-tight leading-tight"
        style={{ fontFamily: "Fraunces, serif", fontWeight: 400 }}
      >
        {profile.name}
      </h3>

      {/* Hairline accent */}
      <div
        className="mx-auto mt-6 mb-7 h-px"
        style={{ width: 56, background: "rgba(192,197,206,0.35)" }}
        aria-hidden
      />

      {/* Discipline keywords */}
      <div
        className="text-center text-[10px] tracking-[0.32em] uppercase mb-6"
        style={{ color: "#9AA4B2" }}
      >
        {profile.discipline}
      </div>

      {/* Body */}
      <p className="text-[#D4D8DF] leading-relaxed text-[15px]">
        {profile.body}
      </p>
    </article>
  );
};

export default Team;
